<template>
  <div>
    <HeaderBreadcrumbs
      :items="[{ label: 'Publisher' }]"
      title="All Publisher"
    />

    <div class="flex-col w-full bg-white rounded-lg">
      <div class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg ">
        <div class="relative w-2/6">
          <SearchOutline className="text-xl absolute top-1/2 left-3 transform -translate-y-1/2" />
          <input
            type="search"
            placeholder="Search Publisher Name, Publisher ID AX"
            class="p-2.5 pl-5 w-full text-sm border rounded-md bg-gray-50"
            v-model="query_search"
            @keyup.enter="onSearch"
            @input="onSearch"
          />
        </div>
        <div class="relative flex justify-end w-3/4">
          <Button
            @click="$router.push('/vendor/create')"
            variant="default"
            color="primary"
            class="mr-3"
          >
            <template #iconBefore>
              <i class="fa fa-plus"></i>
            </template>
            Add New
          </Button>

          <Button
            @click="toggleDropdown"
            variant="outlined"
            color="alternative"
            class="relative"
          >
            <template #iconBefore>
              <FilterFilled className="h-3 w-3 text-gray-800" />
            </template>
            Filter
            <template #iconAfter>
              <ChevronDownFilled className="h-3 w-3 text-gray-500" />
            </template>

            <!-- Filter count badge -->
            <span
              v-if="activeFilterCount > 0"
              class="absolute flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full -top-2 -right-2"
            >
              {{ activeFilterCount }}
            </span>
          </Button>
          <VendorFilter
            :isDropdownVisible.sync="isDropdownVisible"
            :initialFilters="appliedFilters"
            @input="handleFilterInput"
            @reset="handleResetFilter"
          />
        </div>
      </div>
    </div>

    <Table
      :headers="tableHeaders"
      :data="vendors"
      :rowKey="tableRowKeys"
      :currentPage="currentPage"
      :perPage="tablePerPage"
      :isLoading="isLoading"
      :withScrollX="true"
      className="mb-2 w-full"
      @sort="handleTableSort"
    >
      <template #name="{ value, row }">
        <router-link :to="`/vendor/detail/${row?.id}`">
          {{ value ?? '-' }}
        </router-link>
      </template>
      <template #sync_vendor="{ value }">
        {{ value?.vendor_id_ax ?? '-' }}
      </template>
      <template #cas_organization="{ value }">
        {{ value?.name ?? '-' }}
      </template>
      <template #vendor_type="{ row }">
        {{ row.is_external ? 'External' : 'Internal' }}
      </template>
      <template #status="{ row }">
        <div>
          <Label
            :variant="row?.is_active ? 'green' : 'gray'"
          >
            {{ row?.is_active ? 'Active' : 'Inactive' }}
          </Label>
        </div>
      </template>
      <template #created='{ value }'>
        <div>
          {{ formatDate(value) }}
        </div>
        <div class="text-xs italic text-gray-500">
          {{ formatTime(value) }}
        </div>
      </template>
      <template #modified='{ value }'>
        <div>
          {{ formatDate(value) }}
        </div>
        <div class="text-xs italic text-gray-500">
          {{ formatTime(value) }}
        </div>
      </template>
      <template #actions="{ row }">
        <router-link
          :to="{ path: `vendor/edit/${row.id}` }"
          title="Edit"
          class="inline-block mx-auto"
        >
          <EditFilled />
        </router-link>
      </template>
    </Table>

    <div class="flex justify-between pt-4 pl-3 pr-3 mt-1 mb-2 bg-white border-t border-gray-100" v-if="Number(totalRows)">
      <div class="font-semibold">
        <span class="font-normal text-gray-500">Showing </span> {{ currentPage }}-{{ Number(totalRows) >= perPage ? perPage : Number(totalRows) }} <span
          class="font-normal text-gray-500">of</span> {{ parseInt(totalRows) }}
      </div>
      <div class="mb-2">
        <t-pagination :disabled="isLoading" :total-items="Number(totalRows)" :per-page="perPage" v-model="currentPage"
          @change="onClickPage" />
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import { mapState, mapActions } from 'vuex';

  import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
  import SearchOutline from '@/components/icons/SearchOutline';
  import EditFilled from '@/components/icons/EditFilled';
  import Table from '@/components/form/table';
  import Label from '@/components/ui/Label';
  import Button from '@/components/ui/Button';
  import FilterFilled from '@/components/icons/FilterFilled';
  import ChevronDownFilled from '@/components/icons/ChevronDownFilled';
  import VendorFilter from './VendorFilter';

  const PAGE_SIZE = 15;

  export default {
    name: 'VendorNew',
    components: {
      HeaderBreadcrumbs,
      SearchOutline,
      EditFilled,
      Table,
      Label,
      Button,
      FilterFilled,
      ChevronDownFilled,
      VendorFilter
    },
    data() {
      return {
        query_search: null,
        currentPage: 1,
        perPage: PAGE_SIZE,
        sortOrder: [],
        isDropdownVisible: false,
        typing: null,

        // Applied filters state
        appliedFilters: {
          status: null
        },

        // table
        tableHeaders: [
          { label: 'No', key: 'no' },
          { label: 'Publisher Name', key: 'vendor_name', class: "w-2/12" },
          { label: 'Sync Vendor ID AX', key: 'sync_vendor_id_ax', class: "w-2/12" },
          { label: 'Organization', key: 'organization', class: "w-2/12" },
          { label: 'Publisher Type', key: 'vendor_type', class: "w-2/12" },
          { label: 'Status', key: 'status', class: "w-1/12" },
          { label: 'Created Date', key: 'created', class: "w-1/12", sortable: true, style: 'min-width: 120px' },
          { label: 'Last Modified', key: 'modified', class: "w-1/12", sortable: true, style: 'min-width: 120px' }
        ],

        tableRowKeys: [
          'name',
          'sync_vendor',
          'cas_organization',
          'vendor_type',
          'status',
          'created',
          'modified'
        ],

        tablePerPage: PAGE_SIZE,
        activeSort: {
          order_by: null,
          sort: 'ASC'
        }
      }
    },
    watch: {
      currentPage() {
        // window.scrollTo(0, 0)
        this.actionGetVendor();
      }
    },
    computed: {
      ...mapState({
        isLoading: ({ vendors }) => vendors.isLoading,
        isError: ({ vendors }) => vendors.isError,
        totalRows: ({ vendors }) => vendors.totalRows,
        vendors: ({ vendors }) => vendors.items
      }),

      activeFilterCount() {
        let count = 0;
        // Count status filter as active if it has a value and is not null
        if (this.appliedFilters.status && this.appliedFilters.status !== null) count++;
        return count;
      }
    },
    mounted() {
      this.actionGetVendor();
    },
    methods: {
      ...mapActions('vendors', [
        'fetchVendors',
        'searchVendors',
        'fetchVendorsById'
      ]),
      actionGetVendor() {
        const payload = {
          page: this.currentPage,
          limit: this.perPage,
        };
        if (this.status !== null && this.status !== undefined) payload.status = this.status;
        if (this.message) payload.name = this.message;
        this.fetchVendors(payload);
      },
      actionSearchVendor() {
        const payload = {
          page: 1,
          limit: this.perPage
        }

        if (this.status !== null && this.status !== undefined) payload.status = this.status;
        if (this.message) payload.name = this.message;
        if (this.activeSort.order_by) {
          payload.sort = this.activeSort.sort
          payload.order_by = this.activeSort.order_by
        }

        this.fetchVendors(payload);
      },
      toggleDropdown() {
        this.isDropdownVisible = !this.isDropdownVisible;
      },
      onSearch(event) {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => {
          this.message = event.target.value;
          this.actionSearchVendor();
        }, 600);
      },
      handleFilterInput(filterData) {
        // Update applied filters state for indicator
        this.appliedFilters = { ...filterData };
        
        if (filterData.status === 'active') {
          this.status = 'true';
        } else if (filterData.status === 'inactive') {
          this.status = 'false';
        } else {
          this.status = null;
        }
        this.reloadPage()
      },
      handleResetFilter() {
        this.appliedFilters = {
          status: null
        };
        this.status = null;
        this.reloadPage()
      },
      reloadPage() {
        if (!this.query_search && !this.status) {
          this.actionGetVendor();
        } else {
          this.actionSearchVendor();
        }
      },
      formatDate(value) {
        return moment(value).format('DD MMM YYYY')
      },
      formatTime(value) {
        return moment(value).format('HH:mm:ss')
      },
      onClickPage(page) {
        if (this.isLoading) {
          return;
        }
        this.currentPage = page
      },
      handleTableSort(key) {
        const newActiveSort = {...this.activeSort};
        if (!this.activeSort.order_by) {
          newActiveSort.order_by = key;
        } else if (this.activeSort.order_by === key) {
          newActiveSort.sort = newActiveSort.sort === 'DESC' ? 'ASC' : 'DESC'
        } else if (this.activeSort.order_by !== key) {
          newActiveSort.order_by = key;
          newActiveSort.sort = 'ASC'
        }
        this.activeSort = newActiveSort;
        this.actionSearchVendor();
      }
    }
  }
</script>
